// 音频实时播放器（单例模式）
class AudioPlayer {
  private static instance: AudioPlayer | null = null;

  private mediaSource: MediaSource;
  private audio: HTMLAudioElement;
  private audioContext: AudioContext | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private cacheBuffers: ArrayBuffer[] = [];
  private pauseTimer: number | null = null;
  private isManualPaused = false;
  private isDestroyed = false;
  public isPlaying: boolean = false;

  // @ts-ignore
  private startCallback: Function = null;
  // @ts-ignore
  private stopCallback: Function = null;

  private constructor() {
    this.mediaSource = new MediaSource();
    this.audio = new Audio();
    this.initialize();
  }

  public static getInstance(): AudioPlayer {
    if (!AudioPlayer.instance) {
      AudioPlayer.instance = new AudioPlayer();
    }
    return AudioPlayer.instance;
  }

  public onStart(callback: () => void) {
    this.startCallback = callback;
  }

  public onStop(callback: () => void) {
    this.stopCallback = callback;
  }

  private initialize() {
    if (this.isDestroyed) {
      this.mediaSource = new MediaSource();
      this.audio = new Audio();
      this.isDestroyed = false;
    }

    this.audioContext = new AudioContext();
    this.audio.src = URL.createObjectURL(this.mediaSource);
    this.setupAudioContext();
    this.setupMediaSourceListeners();
  }

  private setupAudioContext() {
    if (!this.audioContext) return;

    const source = this.audioContext.createMediaElementSource(this.audio);
    source.connect(this.audioContext.destination);
  }

  private setupMediaSourceListeners() {
    this.mediaSource.addEventListener('sourceopen', () => {
      if (this.sourceBuffer || !this.mediaSource) return;

      try {
        this.sourceBuffer = this.mediaSource.addSourceBuffer('audio/mpeg');
        this.setupSourceBufferListeners();
      } catch (error) {
        console.error('Error creating source buffer:', error);
      }
    });

    this.mediaSource.addEventListener('sourceended', () => {
      this.cleanupResources();
    });
  }

  private setupSourceBufferListeners() {
    if (!this.sourceBuffer) return;

    this.sourceBuffer.addEventListener('update', () => {
      if (this.cacheBuffers.length && !this.sourceBuffer?.updating) {
        const buffer = this.cacheBuffers.shift()!;
        this.sourceBuffer?.appendBuffer(buffer);
      }
      this.scheduleAutoPause();
    });

    this.sourceBuffer.addEventListener('error', () => {
      this.clear();
    });
  }

  public receiveAudioData(audioData: ArrayBuffer): void {
    if (this.isDestroyed || this.isManualPaused) return;

    if (!this.sourceBuffer || this.sourceBuffer.updating) {
      this.cacheBuffers.push(audioData);
    } else {
      try {
        this.sourceBuffer.appendBuffer(audioData);
      } catch (error) {
        console.error('Error appending buffer:', error);
        this.cacheBuffers.push(audioData);
      }
    }

    this.ensurePlayback();
  }

  public pause(): void {
    this.isManualPaused = true;
    this.isPlaying = false;
    this.audio.pause();
    this.clearPauseTimer();
    if (typeof this.stopCallback === 'function') {
      this.stopCallback();
    }
  }

  public resume(): void {
    this.isManualPaused = false;
    this.isPlaying = true;
    this.ensurePlayback();
  }

  public reload(): void {
    // this.pause()
    this.clear();
  }

  private operationQueue: Promise<void> = Promise.resolve();
  private isAborting = false;

  public async clear(): Promise<void> {
    // 进入队列化操作
    this.operationQueue = this.operationQueue
      .then(async () => {
        // 立即停止音频播放
        this.audio.pause();
        this.audio.currentTime = 0;

        // 清空缓存队列
        this.cacheBuffers = [];
        this.clearPauseTimer();
        // 核心清理逻辑
        await this._safeBufferCleanup();

        // 确保音频元素重置
        if (!this.audio.paused) {
          this.audio.load();
        }

        if (typeof this.stopCallback === 'function') {
          this.stopCallback();
        }
      })
      .catch((error) => {
        console.error('清理操作链异常:', error);
      });
    return this.operationQueue;
  }

  private async _safeBufferCleanup(): Promise<void> {
    if (!this.sourceBuffer) return;
    try {
      // 状态检查包装器
      const waitForUpdateEnd = () =>
        new Promise<void>((resolve, reject) => {
          if (!this.sourceBuffer!.updating) {
            return resolve();
          }
          const onUpdateEnd = () => {
            this.sourceBuffer!.removeEventListener('updateend', onUpdateEnd);
            resolve();
          };
          const onError = (e: Event) => {
            this.sourceBuffer!.removeEventListener('error', onError);
            reject(new Error('SourceBuffer 更新失败'));
          };
          this.sourceBuffer!.addEventListener('updateend', onUpdateEnd);
          this.sourceBuffer!.addEventListener('error', onError);
        });
      // 第一步：中止进行中的操作
      if (this.sourceBuffer.updating) {
        this.isAborting = true;
        this.sourceBuffer.abort();
        await waitForUpdateEnd();
        this.isAborting = false;
      }
      // 第二步：物理清理缓冲区
      if (this.sourceBuffer.buffered?.length > 0) {
        const end = this.sourceBuffer.buffered.end(0);
        if ('remove' in this.sourceBuffer) {
          this.sourceBuffer.remove(0, end);
          await waitForUpdateEnd();
        }
      }
      // 第三步：安全设置时间戳
      if (!this.isAborting) {
        this.sourceBuffer.timestampOffset = 0;
        await waitForUpdateEnd();
      }
      // 第四步：重置媒体时长
      if (this.mediaSource.readyState === 'open') {
        this.mediaSource.duration = 0;
      }
    } catch (error) {
      console.warn('安全清理失败，启动应急方案:', error);
      await this._rebuildMediaSource();
    }
  }

  private async _rebuildMediaSource(): Promise<void> {
    // 渐进式重建方案
    const oldMediaSource = this.mediaSource;
    this.mediaSource = new MediaSource();
    this.sourceBuffer = null;

    // 防止 URL 泄露
    URL.revokeObjectURL(this.audio.src);
    this.audio.src = URL.createObjectURL(this.mediaSource);

    // 延迟关闭旧媒体源
    setTimeout(() => {
      if (oldMediaSource.readyState !== 'closed') {
        oldMediaSource.endOfStream();
      }
    }, 1000);
  }

  public destroy(): void {
    this.isPlaying = false;
    this.pause();
    this.clear();

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaSource.readyState !== 'closed') {
      this.mediaSource.endOfStream();
      this.mediaSource.duration = 0;
    }

    URL.revokeObjectURL(this.audio.src);
    this.audio.removeAttribute('src');
    this.isDestroyed = true;
  }

  private ensurePlayback(): void {
    if (this.audio.paused && !this.isManualPaused) {
      this.audio
        .play()
        .then(() => {
          this.isPlaying = true
          if (typeof this.startCallback === 'function') {
            this.startCallback();
          }
        })
        .catch((error) => {
          console.error('Playback failed:', error);
          setTimeout(() => this.ensurePlayback(), 1000);
        });
    }
  }

  private scheduleAutoPause(): void {
    this.clearPauseTimer();
    if (!this.sourceBuffer) return;

    const remainingTime = this.sourceBuffer.timestampOffset - this.audio.currentTime;
    this.pauseTimer = window.setTimeout(() => {
      if (!this.isManualPaused && this.cacheBuffers.length === 0) {
        this.audio.pause();
      }
    }, remainingTime * 1000 + 5000);
  }

  private clearPauseTimer(): void {
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
  }

  private cleanupResources(): void {
    URL.revokeObjectURL(this.audio.src);
    this.audio.removeAttribute('src');
  }
}

// 使用示例
export const audioPlayer = AudioPlayer.getInstance();
